/**
 * Data hook for the single-requirement read-only detail view (/requests/:id).
 * Calls GET /api/requirements/{id}/. Surfaces a dedicated `notFound` flag
 * (distinct from a generic `error`) so RequirementDetailPage can render a
 * specific "Requirement not found" state on 404 instead of a red error banner.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError, requirements } from '@/helpers/ApiHelper';
import type { RequirementOut } from '@/types/requirement';

export interface UseRequirementResult {
  data: RequirementOut | null;
  isLoading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => void;
}

export function useRequirement(id: string | undefined): UseRequirementResult {
  const [data, setData] = useState<RequirementOut | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);

  const refetch = useCallback(() => setRefetchToken((t) => t + 1), []);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setNotFound(false);

    requirements
      .get(id)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
        } else {
          setError(e instanceof ApiError ? e.detail : 'Failed to load requirement');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, refetchToken]);

  return { data, isLoading, error, notFound, refetch };
}
